// src/services/pftPdfService.js
//
// Opens an uploaded PFT PDF for a person to re-check values. The file lives in
// the private PFT bucket (REACT_APP_PFT_UPLOAD_BUCKET), not the portal's main
// storage. It is fetched with getBlob, which goes through the bucket's storage
// rules (storage.pft.rules: admins and coordinators of that site, with a valid
// 2-step session) and creates NO lasting download link: the browser keeps the
// file in memory and shows it in a new tab.

import { getApp } from "firebase/app";
import { getBlob, getStorage, ref } from "firebase/storage";
import { PFT_UPLOAD_BUCKET } from "../config/featureFlags";

/**
 * Open `file` (a bucket path like "uploads/lung-innovations/pft/x.pdf").
 * The tab is opened BEFORE the download starts, so pop-up blockers allow it.
 * Returns true on success; throws a short, staff-readable error otherwise.
 */
export const openPftPdf = async (file) => {
  if (!PFT_UPLOAD_BUCKET) throw new Error("PFT storage isn't set up in this environment.");
  if (!file) throw new Error("No PDF is linked to this PFT.");
  const tab = window.open("", "_blank");
  try {
    const storage = getStorage(getApp(), `gs://${PFT_UPLOAD_BUCKET}`);
    const blob = await getBlob(ref(storage, file));
    const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
    if (tab) tab.location.href = url;
    else window.location.assign(url);
    // Release the in-memory copy once the tab has had time to load it.
    setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
    return true;
  } catch (err) {
    if (tab) tab.close();
    const code = err && err.code;
    if (code === "storage/unauthorized") throw new Error("You don't have access to this PDF.");
    if (code === "storage/object-not-found") throw new Error("This PDF is no longer in storage.");
    throw new Error("Couldn't open the PDF. Please try again.");
  }
};
